import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Service } from "@/lib/types";
import { ServiceTile } from "@/components/ServiceTile";
import { LayoutChooser } from "@/components/LayoutChooser";
import { DashboardGridLayout } from "@/components/DashboardGridLayout";
import { DashboardFreeformLayout } from "@/components/DashboardFreeformLayout";
import {
  defaultLayout,
  loadLayout,
  saveLayout,
  type DashboardLayout,
  type LayoutMode,
} from "@/lib/dashboardLayout";

// Owns the per-event tile layout (mode + geometry) and renders one of three
// arrangements. On phone-width viewports every mode collapses to the auto
// stack — a fixed 10-col grid or a pixel canvas is unusable that narrow.

// Sortable wrapper for auto mode. 8px PointerSensor threshold lets a plain tap
// fall through to ServiceTile's onClick; only a real drag reorders.
function SortableServiceTile({
  svc,
  onClick,
}: {
  svc: Service;
  onClick: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: svc.system_id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 10 : "auto",
        cursor: isDragging ? "grabbing" : undefined,
      }}
      {...attributes}
      {...listeners}
    >
      <ServiceTile staticService={svc} onClick={onClick} />
    </div>
  );
}

function AutoLayout({
  services,
  order,
  onReorder,
  onOpen,
}: {
  services: Service[];
  order: string[];
  onReorder: (next: string[]) => void;
  onOpen: (systemId: string) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Saved order first (skipping removed services), new services appended.
  const ordered = useMemo<Service[]>(() => {
    if (order.length === 0) return services;
    const byId = new Map(services.map((s) => [s.system_id, s]));
    const out: Service[] = [];
    for (const id of order) {
      const svc = byId.get(id);
      if (svc) {
        out.push(svc);
        byId.delete(id);
      }
    }
    for (const svc of services) if (byId.has(svc.system_id)) out.push(svc);
    return out;
  }, [services, order]);

  function onDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = ordered.map((s) => s.system_id);
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(ids, from, to));
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={ordered.map((s) => s.system_id)}
        strategy={rectSortingStrategy}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {ordered.map((svc) => (
            <SortableServiceTile
              key={svc.system_id}
              svc={svc}
              onClick={() => onOpen(svc.system_id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export function DashboardTiles({
  services,
  windowId,
  onOpen,
}: {
  services: Service[];
  windowId: string;
  onOpen: (systemId: string) => void;
}) {
  const [layout, setLayout] = useState<DashboardLayout>(defaultLayout);

  // Reload layout whenever the selected event changes.
  useEffect(() => {
    setLayout(loadLayout(windowId));
  }, [windowId]);

  // Narrow-viewport detection — grid/freeform fall back to the auto stack.
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const apply = () => setNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  // Single persist path: update in-memory state and localStorage together.
  const update = useCallback(
    (patch: Partial<DashboardLayout>) => {
      setLayout((prev) => {
        const next = { ...prev, ...patch };
        saveLayout(windowId, next);
        return next;
      });
    },
    [windowId],
  );

  const effectiveMode: LayoutMode = narrow ? "auto" : layout.mode;

  return (
    <div className="space-y-3">
      <LayoutChooser
        mode={layout.mode}
        cols={layout.gridCols}
        rows={layout.gridRows}
        narrow={narrow}
        onModeChange={(m) => update({ mode: m })}
        onColsChange={(v) => update({ gridCols: v })}
        onRowsChange={(v) => update({ gridRows: v })}
      />

      {effectiveMode === "auto" && (
        <AutoLayout
          services={services}
          order={layout.autoOrder}
          onReorder={(next) => update({ autoOrder: next })}
          onOpen={onOpen}
        />
      )}

      {effectiveMode === "grid" && (
        <DashboardGridLayout
          services={services}
          cols={layout.gridCols}
          rows={layout.gridRows}
          gridCells={layout.gridCells}
          onGridCellsChange={(next) => update({ gridCells: next })}
          onOpen={onOpen}
        />
      )}

      {effectiveMode === "freeform" && (
        <DashboardFreeformLayout
          services={services}
          positions={layout.freeform}
          onPositionsChange={(next) => update({ freeform: next })}
          onOpen={onOpen}
        />
      )}
    </div>
  );
}
